import { useEffect, useState } from 'react'
import { useAgentReferenceHtml } from '@/agent/references/hooks/useAgentReferenceHtml'
import { AgentReferenceType } from '@/agent/references/store/schemas'
import ContentViewerErrorState from '../../common/ContentViewerErrorState'
import HTMLBaseRenderer from '../../base-renderers/HTMLBaseRenderer'
import CaseLawReferenceCitatorTab from '@/citator/views/CaseLawReferenceCitatorTab'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { trackRiaSourceViewerTabChanged, type RiaSourceContext } from '@/analytics/utils/ria-source-analytics'
import { trackCitatorPanelOpened } from '@/analytics/utils/citator-analytics'
import { RiaViewerTab } from '@/analytics/enums/ria.enums'
import { CitatorPanelSource, CitatorSurface } from '@/analytics/enums/citator.enums'
import { CaseLawReferenceTab } from '@/citator/schema/case-law-reference-tab'
import { HtmlReferenceLoadingSkeleton } from '@/chat-common/components/reference-viewer/caselaw/CaseLawOpinionLoadingSkeleton'

type CaseLawRendererProps = {
  s3_path: string
  highlightSentences?: string[]
  parentId?: string
  riaContext?: RiaSourceContext | null
  defaultTab?: CaseLawReferenceTab
  resetKey?: string
}

/**
 * CaseLawRenderer Component
 *
 * Renders caselaw references with citator functionality.
 * - Fetches HTML content from API
 * - Provides tabbed interface (Source View / Citator)
 * - Uses pure HTMLBaseRenderer for HTML display
 *
 * @param props.s3_path - S3 directory path for the caselaw document
 * @param props.highlightSentences - Optional sentences to highlight in the HTML
 * @param props.parentId - Caselaw parent identifier for citator functionality
 */
export default function CaseLawRenderer(props: CaseLawRendererProps): JSX.Element {
  const { s3_path, highlightSentences, parentId, defaultTab, resetKey } = props
  const { trackEvent } = useAnalytics()
  const riaContext = props.riaContext ?? null
  const citatorParentId = parentId ?? ''

  const initialTab = defaultTab ?? CaseLawReferenceTab.source
  const [activeTab, setActiveTab] = useState<CaseLawReferenceTab>(initialTab)

  useEffect(() => {
    setActiveTab(initialTab)
  }, [initialTab, resetKey, s3_path])

  // Fetch HTML content from API
  // Hook is only called when this component is mounted (CASELAW type)
  const { htmlContent, isLoading, isError, retry } = useAgentReferenceHtml(AgentReferenceType.CASELAW, s3_path)

  // Styles for tabs
  const inactiveTabStyles =
    'grow border-transparent text-brand-neutral-500 hover:bg-brand-neutral-100 hover:text-brand-500 w-1/2 border-b-2 py-4 px-1 text-center text-sm font-medium'
  const activeTabStyles = 'grow border-brand-800 text-brand-800 w-1/2 border-b-2 py-4 px-1 text-center text-sm font-medium'

  return (
    <>
      {/* Tabs */}
      <div className="border-b px-4">
        <nav className="w-full flex items-center" aria-label="Tabs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === CaseLawReferenceTab.source}
            onClick={() => {
              if (activeTab !== CaseLawReferenceTab.source && riaContext) {
                trackRiaSourceViewerTabChanged({
                  trackEvent,
                  context: riaContext,
                  fromTab: RiaViewerTab.Citator,
                  toTab: RiaViewerTab.SourceView,
                })
              }
              setActiveTab(CaseLawReferenceTab.source)
            }}
            className={activeTab === CaseLawReferenceTab.source ? activeTabStyles : inactiveTabStyles}
          >
            Opinion
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === CaseLawReferenceTab.citator}
            onClick={() => {
              if (activeTab !== CaseLawReferenceTab.citator) {
                if (riaContext) {
                  trackRiaSourceViewerTabChanged({
                    trackEvent,
                    context: riaContext,
                    fromTab: RiaViewerTab.SourceView,
                    toTab: RiaViewerTab.Citator,
                  })
                }
                // Tab-driven open; badge-driven opens fire from the badge CTA.
                trackCitatorPanelOpened({
                  trackEvent,
                  surface: CitatorSurface.Chat,
                  source: CitatorPanelSource.CitationClick,
                })
              }
              setActiveTab(CaseLawReferenceTab.citator)
            }}
            className={activeTab === CaseLawReferenceTab.citator ? activeTabStyles : inactiveTabStyles}
          >
            Citation Analysis
          </button>
        </nav>
      </div>

      {/* Content Area */}
      <div className="flex-grow overflow-y-auto h-full w-full">
        {/* Render Source View using pure HTMLBaseRenderer */}
        {activeTab === CaseLawReferenceTab.source && isLoading && <HtmlReferenceLoadingSkeleton />}

        {activeTab === CaseLawReferenceTab.source && !isLoading && (isError || !htmlContent) && (
          <ContentViewerErrorState message="Failed to load caselaw reference." onRetry={retry} />
        )}

        {activeTab === CaseLawReferenceTab.source && !isLoading && !isError && htmlContent && (
          <div className="h-full">
            <HTMLBaseRenderer htmlContent={htmlContent} highlightSentences={highlightSentences} />
          </div>
        )}

        {/* Render Citator View */}
        {activeTab === CaseLawReferenceTab.citator && (
          <div className="h-full">
            <CaseLawReferenceCitatorTab parentId={citatorParentId} analyticsContext={riaContext ?? undefined} citatorSurface={CitatorSurface.Chat} />
          </div>
        )}
      </div>
    </>
  )
}
